import { analyticsManager, EcommerceItem, TransactionEventParams } from '../analytics';

/**
 * Create ecommerce item from transaction data
 */
const createTransactionItem = (
  itemId: string,
  itemName: string,
  price: number,
  currency: string,
  quantity: number = 1
): EcommerceItem => ({
  item_id: itemId,
  item_name: itemName,
  item_category: 'transaction',
  price: price,
  currency: currency,
  quantity: quantity,
});

/**
 * Track transaction list view
 */
export const trackViewTransactionList = (
  transactionCount?: number,
  totalValue?: number,
  currency?: string,
  parentAccountId?: string
) => {
  const items: EcommerceItem[] = [];
  
  if (totalValue !== undefined && currency) {
    items.push(createTransactionItem(
      'transaction_list',
      `All Transactions (${transactionCount || 0})`,
      totalValue,
      currency,
      transactionCount || 1
    ));
  }

  analyticsManager.trackTransactionEvent('view_list', items, {
    value: totalValue || 0,
    currency: currency || 'USD',
    parent_account_id: parentAccountId,
  });
};

/**
 * Track individual transaction view
 */
export const trackViewTransaction = (
  transactionId: string,
  amount: number,
  currency: string,
  transactionType?: string,
  parentAccountId?: string,
  propertyId?: string
) => {
  const item = createTransactionItem(
    transactionId,
    `Transaction ${transactionId}`,
    amount,
    currency,
    1
  );

  analyticsManager.trackTransactionEvent('view', [item], {
    transaction_id: transactionId,
    value: amount,
    currency: currency,
    transaction_type: transactionType,
    parent_account_id: parentAccountId,
    property_id: propertyId,
  });
};

/**
 * Track invoice list view
 */
export const trackViewInvoiceList = (
  invoiceCount?: number,
  totalValue?: number,
  currency?: string,
  parentAccountId?: string
) => {
  const items: EcommerceItem[] = [];
  
  if (totalValue !== undefined && currency) {
    items.push(createTransactionItem(
      'invoice_list',
      `All Invoices (${invoiceCount || 0})`,
      totalValue,
      currency,
      invoiceCount || 1
    ));
  }

  analyticsManager.trackTransactionEvent('view_list', items, {
    value: totalValue || 0,
    currency: currency || 'USD',
    parent_account_id: parentAccountId,
  });
};

/**
 * Track individual invoice view
 */
export const trackViewInvoice = (
  invoiceId: string,
  amount: number,
  currency: string,
  invoiceStatus?: string,
  parentAccountId?: string,
  propertyId?: string
) => {
  const item = createTransactionItem(
    invoiceId,
    `Invoice ${invoiceId}`,
    amount,
    currency,
    1
  );

  analyticsManager.trackTransactionEvent('view', [item], {
    transaction_id: invoiceId,
    value: amount,
    currency: currency,
    transaction_type: invoiceStatus || 'pending',
    parent_account_id: parentAccountId,
    property_id: propertyId,
  });
};

/**
 * Track payment initialization (begin checkout)
 */
export const trackBeginCheckout = (
  transactionId: string,
  amount: number,
  currency: string,
  items?: Array<{ id: string; name: string; price: number; quantity?: number }>,
  parentAccountId?: string,
  propertyId?: string,
  coupon?: string
) => {
  const ecommerceItems = items?.map(item =>
    createTransactionItem(item.id, item.name, item.price, currency, item.quantity || 1)
  ) || [createTransactionItem(transactionId, `Payment: ${transactionId}`, amount, currency)];

  analyticsManager.trackTransactionEvent('begin_checkout', ecommerceItems, {
    transaction_id: transactionId,
    value: amount,
    currency: currency,
    parent_account_id: parentAccountId,
    property_id: propertyId,
    coupon: coupon,
  });
};

/**
 * Track successful payment (purchase complete)
 */
export const trackCompletePurchase = (
  transactionId: string,
  amount: number,
  currency: string,
  paymentMethod?: string,
  items?: Array<{ id: string; name: string; price: number; quantity?: number }>,
  parentAccountId?: string,
  propertyId?: string,
  coupon?: string
) => {
  const ecommerceItems = items?.map(item =>
    createTransactionItem(item.id, item.name, item.price, currency, item.quantity || 1)
  ) || [createTransactionItem(transactionId, `Payment: ${transactionId}`, amount, currency)];

  analyticsManager.trackTransactionEvent('purchase', ecommerceItems, {
    transaction_id: transactionId,
    value: amount,
    currency: currency,
    payment_method: paymentMethod,
    parent_account_id: parentAccountId,
    property_id: propertyId,
    coupon: coupon,
  });
};

/**
 * Track payment error/failure
 */
export const trackPaymentError = (
  transactionId: string,
  amount: number,
  currency: string,
  errorCode?: string,
  errorMessage?: string,
  parentAccountId?: string,
  propertyId?: string
) => {
  analyticsManager.trackCustomEvent('payment_error', {
    transaction_id: transactionId,
    value: amount,
    currency: currency,
    error_code: errorCode,
    error_message: errorMessage,
    parent_account_id: parentAccountId,
    property_id: propertyId,
  });
};

/**
 * Track transaction refund
 */
export const trackTransactionRefund = (
  transactionId: string,
  amount: number,
  currency: string,
  refundReason?: string,
  parentAccountId?: string,
  propertyId?: string
) => {
  analyticsManager.trackCustomEvent('transaction_refunded', {
    transaction_id: transactionId,
    refund_amount: amount,
    currency: currency,
    refund_reason: refundReason,
    parent_account_id: parentAccountId,
    property_id: propertyId,
  });
};

/**
 * Track invoice download
 */
export const trackInvoiceDownload = (
  invoiceId: string,
  amount: number,
  currency: string,
  parentAccountId?: string,
  propertyId?: string
) => {
  analyticsManager.trackCustomEvent('invoice_downloaded', {
    invoice_id: invoiceId,
    value: amount,
    currency: currency,
    parent_account_id: parentAccountId,
    property_id: propertyId,
  });
};

/**
 * Track invoice email sent
 */
export const trackInvoiceEmailSent = (
  invoiceId: string,
  recipientEmail?: string,
  parentAccountId?: string,
  propertyId?: string
) => {
  analyticsManager.trackCustomEvent('invoice_email_sent', {
    invoice_id: invoiceId,
    recipient_email: recipientEmail,
    parent_account_id: parentAccountId,
    property_id: propertyId,
  });
};

/**
 * Track billing filter/sort applied
 */
export const trackBillingFilterApplied = (
  filterType: string,
  filterValue: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('billing_filter_applied', {
    filter_type: filterType,
    filter_value: filterValue,
    parent_account_id: parentAccountId,
  });
};

/**
 * Track billing date range change
 */
export const trackBillingDateRangeChanged = (
  startDate: string,
  endDate: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('billing_date_range_changed', {
    start_date: startDate,
    end_date: endDate,
    parent_account_id: parentAccountId,
  });
};

/**
 * Track transaction export
 */
export const trackTransactionExport = (
  format: 'csv' | 'pdf' | 'json',
  transactionCount?: number,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('transaction_export', {
    format: format,
    transaction_count: transactionCount || 0,
    parent_account_id: parentAccountId,
  });
};

/**
 * Track coupon applied
 */
export const trackCouponApplied = (
  couponCode: string,
  discount: number,
  currency: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('coupon_applied', {
    coupon_code: couponCode,
    discount: discount,
    currency: currency,
    parent_account_id: parentAccountId,
  });
};

/**
 * Track coupon removed
 */
export const trackCouponRemoved = (
  couponCode: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('coupon_removed', {
    coupon_code: couponCode,
    parent_account_id: parentAccountId,
  });
};
